import {useDispatch} from "react-redux";
import "./style.css";
import * as React from "react";
import {useState} from "react";
import {useSearchParams} from "react-router-dom";
import dayjs from "dayjs";
import {Checkbox, TableCell, TableRow} from "@mui/material";
import TextInput from "../../../components/elements/TextInput";
import {currencyFormatter, formatDate} from "../../../utils";
import SubmitButton from "../../../components/elements/SubmitButton";
import {kickoffSyncs, updateFacilityETL} from "../../../redux/models/integrations/Action";
import {ABORTED} from "../../../redux/constants";
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider/LocalizationProvider.js";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs/AdapterDayjs.js";
import {DatePicker} from "@mui/x-date-pickers/DatePicker/DatePicker.js";


export const FacilityETL = ({
    loggedInUser,
    integrationNamespace,
    integrationAccount,
    facility,
    facilityETL,
    etl,
    currentUsage,
    isActive
}) => {
    const dispatch = useDispatch();
    const [isChecked, setIsChecked] = useState(facilityETL.is_active);
    const [formState, setFormState] = useState({});
    const [errors, setErrors] = useState({
        maximum_monthly_calls: ""
    });

    const today = new Date();
    const lookback = (new Date()).setDate(today.getDate() - 1);
    const defaultStart = dayjs(lookback);
    const defaultEnd = dayjs(today);
    const [startDate, setStartDate] = useState(defaultStart);
    const [endDate, setEndDate] = useState(defaultEnd);

    const maximumMonthlyCalls = isActive || isChecked ? formState.maximum_monthly_calls !== undefined ? formState.maximum_monthly_calls : facilityETL.maximum_monthly_calls : 0;
    const currentMonthlyCalls = currentUsage.reduce((acc, usage) => {
        return acc + (usage.facility_id === facilityETL.facility_id && usage.etl_id === facilityETL.etl_id ? usage.calls : 0);
    }, 0);

    const disableSync = !isActive || !isChecked || currentMonthlyCalls.call >= maximumMonthlyCalls;

    return <TableRow>
        <TableCell>{etl.name}</TableCell>
        <TableCell style={{
            display: "flex",
            alignItems: "anchor-center",
            color: currentMonthlyCalls >= maximumMonthlyCalls && currentMonthlyCalls > 0 ? "red" : "inherit"
        }}>
            <span>{currentMonthlyCalls} out of &nbsp;</span>
            <span>
                <TextInput
                    name={"maximum_monthly_calls"}
                    label={"Maximum Monthly Quota"}
                    formState={formState}
                    errors={errors}
                    setErrors={setErrors}
                    setFormState={setFormState}
                    maximum_monthly_calls={formState.maximum_monthly_calls || facilityETL.maximum_monthly_calls}
                    autoComplete={"off"}
                    disabled={!isActive}
                />
            </span>
        </TableCell>
        <TableCell>
            <Checkbox
                disabled={!isActive}
                checked={formState.is_active === undefined ? facilityETL.is_active : formState.is_active}
                onChange={(e) => {
                    setFormState(state => {
                        if (e.target.checked) {
                            return {
                                ...state,
                                is_active: e.target.checked
                            };
                        }
                        return {
                            ...state,
                            maximum_monthly_calls: 0,
                            is_active: e.target.checked
                        };
                    });
                }}/>
        </TableCell>
        <TableCell>
            {facilityETL.status}
        </TableCell>
        <TableCell>
            {currencyFormatter.format(currentMonthlyCalls * 0.002 * 1.4)}
        </TableCell>
        <TableCell>
            {currencyFormatter.format((currentMonthlyCalls.calls > maximumMonthlyCalls ? currentMonthlyCalls : maximumMonthlyCalls) * 0.002 * 1.4)}
        </TableCell>
        <TableCell>
            <LocalizationProvider key={"loc-provider-start" + facilityETL.id} dateAdapter={AdapterDayjs}>
                <DatePicker style={{width: "100%"}} label="From" value={dayjs(startDate)}
                    disabled={disableSync}
                    onChange={(newDate) => {
                        setStartDate(dayjs(newDate));
                    }}/>
            </LocalizationProvider>
            {errors?.start_date ? <span style={{color: "red"}}>{errors.start_date}</span> : ""}
        </TableCell>
        <TableCell>
            <LocalizationProvider key={"loc-provider-end" + facilityETL.id} dateAdapter={AdapterDayjs}>
                <DatePicker style={{width: "100%"}} label="Until" value={dayjs(endDate)}
                    disabled={disableSync}
                    onChange={(newDate) => {
                        setEndDate(dayjs(newDate));
                    }}/>
            </LocalizationProvider>
            {errors?.end_date ? <span style={{color: "red"}}>{errors.end_date}</span> : ""}
        </TableCell>
        <TableCell>
            <SubmitButton
                disabled={disableSync}
                onClick={() => {
                    setErrors({start_date: "", end_date: ""});
                    return dispatch(kickoffSyncs({
                        name: etl.name,
                        organization_id: facility.organization_id,
                        integration_account_id: integrationAccount.id,
                        namespace: integrationNamespace,
                        start_date: formatDate(startDate),
                        end_date: formatDate(endDate),
                        updated_at: new Date().toISOString(),
                        owner_id: loggedInUser.id,
                        facility_license_number: facility.license_number,
                        etl_id: facilityETL.etl_id
                    })
                    ).then(updatedFacilityETL => {
                        setIsChecked(updatedFacilityETL.is_active);
                        setErrors({});
                        setFormState(updatedFacilityETL);
                    }).catch(err => {
                        if (err === ABORTED) {
                            return;
                        }
                        setErrors(Object.entries(err).reduce((agg, [fieldName, messages]) => {
                            if (!Array.isArray(messages)) {
                                messages = [messages];
                            }
                            return {
                                ...agg,
                                [fieldName]: "" + messages.join(", ")
                            };
                        }, {}));
                    });
                }}
                variant={"contained"}
                color={"primary"}
                size={"small"}
                style={{
                    marginLeft: "10px"
                }}
            >Sync</SubmitButton>

        </TableCell>
        <TableCell>
            <SubmitButton
                onClick={() => {
                    return dispatch(updateFacilityETL(
                        integrationNamespace,
                        {
                            ...facilityETL,
                            ...formState,
                        },
                    )).then(updatedFacilityETL => {
                        setIsChecked(updatedFacilityETL.is_active);
                        setErrors({});
                        setFormState(updatedFacilityETL);
                    }).catch(err => {
                        if (err === ABORTED) {
                            return;
                        }
                        setErrors(Object.entries(err).reduce((agg, [fieldName, messages]) => {
                            if (!Array.isArray(messages)) {
                                messages = [messages];
                            }
                            return {
                                ...agg,
                                [fieldName]: "" + messages.join(", ")
                            };
                        }, {}));
                    });
                }}
                variant={"contained"}
                color={"primary"}
                size={"small"}
                style={{
                    marginLeft: "10px"
                }}
            >Save</SubmitButton>
        </TableCell>
    </TableRow>;
};